// lib/heldPhotoOptions.mjs — R114 fix2 (review: "Recovered photos: a group
// keyed by a wrong suggested_target offers only that wrong property's
// inspections, so the correct inspection can no longer be picked"). Which
// inspections the office's Recovered-photos card offers for ONE held group,
// in order. Pure — the card (client/src/pages/InspectionsPage.ts) runs this
// exact code and `node lib/heldPhotoOptions.test.mjs` tests it.
//
// A group's suggestion is a time-window guess: with two inspectors at two
// properties at once it is often the OTHER visit, and a "possible misfile"
// can be a false alarm. So the suggestion is pre-selected but never narrows
// the choice:
//   1. search matches, when the office typed a query — across every
//      inspection the page knows (the whole portfolio), not just recent ones
//   2. the suggested target (pre-selected)
//   3. the inspection(s) the photos were queued under
//   4. the group's property, then every other property its photos point at
//      (a phone's listing hint, a misfile's queued-under property)
//   5. the newest inspections across the portfolio
// Each inspection appears once (first place wins).

const str = (v) => (v == null ? '' : String(v));

/** Every brief the payload carries (+ opts.all), by id — the first copy wins. */
function pool(g, payload, all) {
  const out = new Map();
  const add = (b) => { if (b && b.id != null && !out.has(str(b.id))) out.set(str(b.id), b); };
  for (const b of (Array.isArray(all) ? all : [])) add(b);
  for (const b of ((g && g.queued_under) || [])) add(b);
  const cands = (payload && payload.candidates) || {};
  for (const lid of Object.keys(cands)) for (const b of (cands[lid] || [])) add(b);
  for (const b of ((payload && payload.recent) || [])) add(b);
  return out;
}

const statusWord = (s) => (s === 'complete' ? 'complete' : 'in progress');

/** One line per inspection: [property · ]day · status[ · inspector]. */
export function inspectionLine(b, opts = {}) {
  const fmt = typeof opts.fmtDay === 'function' ? opts.fmtDay : (d) => str(d);
  const parts = [];
  if (opts.withProperty !== false) parts.push(str(b.listing_name) || (b.listing_id != null ? `Listing ${b.listing_id}` : 'Unknown property'));
  if (b.visit_day) parts.push(fmt(b.visit_day));
  parts.push(statusWord(b.status));
  if (b.inspector) parts.push(str(b.inspector));
  return parts.join(' · ');
}

/** Inspections matching every word of `query` (property, inspector, day, status, id), newest visit first. */
export function searchInspections(list, query, opts = {}) {
  const words = str(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const fmt = typeof opts.fmtDay === 'function' ? opts.fmtDay : (d) => str(d);
  const hits = [];
  for (const b of (Array.isArray(list) ? list : [])) {
    if (!b || b.id == null) continue;
    const hay = [b.listing_name, b.listing_id, b.inspector, b.id, b.visit_day, b.visit_day ? fmt(b.visit_day) : '', statusWord(b.status)].map(str).join(' ').toLowerCase();
    if (words.every(w => hay.includes(w))) hits.push(b);
  }
  hits.sort((a, b) => str(b.visit_day).localeCompare(str(a.visit_day)));
  return hits.slice(0, Number(opts.limit) > 0 ? Number(opts.limit) : 25);
}

/**
 * The card's choices for group `g` of the GET /inspections/orphan-frames
 * answer `payload`.
 * opts.all      every inspection the page knows, as briefs ({id, listing_id,
 *               listing_name, status, visit_day, inspector}) — the search pool
 * opts.query    the office's search text ('' → no search group)
 * opts.selected the id the office already picked (undefined → the suggestion)
 * opts.fmtDay   'YYYY-MM-DD' → display text
 * → [{ label, items: [{ id, text, selected, disabled? }] }]
 */
export function heldTargetGroups(g, payload, opts = {}) {
  const known = pool(g, payload, opts.all);
  const seen = new Set();
  const sug = g && g.suggested_target && g.suggested_target.id != null ? str(g.suggested_target.id) : null;
  const want = opts.selected === undefined ? sug : str(opts.selected);
  const groups = [];
  const take = (label, list, withProperty = true) => {
    const items = [];
    for (const b of list) {
      if (!b || b.id == null || seen.has(str(b.id))) continue;
      seen.add(str(b.id));
      items.push({ id: str(b.id), text: inspectionLine(b, { ...opts, withProperty }), selected: !!want && str(b.id) === want });
    }
    if (items.length) groups.push({ label, items });
  };
  const q = str(opts.query).trim();
  if (q) {
    const found = searchInspections([...known.values()], q, opts);
    if (found.length) take(`Search results (${found.length})`, found);
    else groups.push({ label: 'Search results', items: [{ id: '', text: `No inspection matches “${q}”`, selected: false, disabled: true }] });
  }
  if (sug && !seen.has(sug)) {
    const b = known.get(sug);
    const st = g.suggested_target;
    seen.add(sug);
    groups.push({ label: 'Suggested (from when the photos were taken)', items: [{
      id: sug,
      text: b ? inspectionLine(b, opts) : (str(st.listing_name) || (st.listing_id != null ? `Listing ${st.listing_id}` : sug)),
      selected: want === sug,
    }] });
  }
  take('Queued under (looked misfiled)', (g && g.queued_under) || []);
  const cands = (payload && payload.candidates) || {};
  const lids = [];
  for (const l of [g && g.listing_id, ...((g && g.other_listing_ids) || [])]) if (l != null && str(l) && !lids.includes(str(l))) lids.push(str(l));
  for (const lid of lids) {
    const list = cands[lid] || [];
    const name = (list.find(b => b && b.listing_name) || {}).listing_name || (g && str(g.listing_id) === lid ? g.listing_name : null) || `Listing ${lid}`;
    take(name, list, false);
  }
  take('Other recent inspections — all properties', (payload && payload.recent) || []);
  // a pick that is in none of the lists (found by an earlier search) stays picked
  if (want && !seen.has(want)) {
    const b = known.get(want);
    groups.unshift({ label: 'Picked', items: [{ id: want, text: b ? inspectionLine(b, opts) : want, selected: true }] });
  }
  return groups;
}
